import {
    CopyIcon,
    PencilIcon,
    TrashIcon,
    UsersIcon,
    UserRoundX,
} from 'lucide-react';
import type { ItemMenuAction, ItemMenuSeparator } from '../item-menu/ItemMenu';
import { ItemMenu } from '../item-menu/ItemMenu';
import { useBuildHubDelete } from '../../hooks/use-build-hub-delete';
import { useBuildHubLeave } from '../../hooks/use-build-hub-leave';
import { useEffect, useState } from 'react';
import { AlertDialog } from '@rocco/ui/dialog/components/AlertDialog';
import { copyToClipboard } from '~/modules/social-sharing/utils/sharing';
import { makeBuildHubUrl } from '../../utils/url-builder';
import { toast } from 'sonner';
import { useSetAtom } from 'jotai';
import { uiStateAtomManageCollabratorModalIsOpened } from '../build-hub-admin/ManageCollabratorModal';
import { uiStateAtomEditBuildHubModal } from '../build-hub-admin/edit-modal-state';
import type { BuildHubUserPermission } from '@modules/build-hub/types';
import { useUser } from '@modules/user';

/**
 * BuildHubListItemMenu is a component that displays a menu for a build hub item.
 * It is used to display the menu for a build hub item in the build hub list page.
 * Defines the actions that can be performed on a build hub item with proper error handling.
 *
 * @param buildHubId - The ID of the build hub
 * @param buildHubUrl
 * @param userPermissions - permissions the current user has on the BuildHub
 * @param isOwner
 * @returns The BuildHubListItemMenu component.
 */
export const BuildHubDetailsHeaderMenu = ({
    buildHubId,
    buildHubUrl,
    userPermissions,
    ownerId,
}: {
    buildHubId: string;
    buildHubUrl: string;
    userPermissions: BuildHubUserPermission;
    ownerId: number;
}) => {
    const user = useUser();
    const isOwner = ownerId === user.ID;

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [openLeaveConfirmation, setOpenLeaveConfirmation] = useState(false);

    const setIsManageCollabratorModalOpen = useSetAtom(
        uiStateAtomManageCollabratorModalIsOpened
    );
    const setIsEditBuildHubModal = useSetAtom(uiStateAtomEditBuildHubModal);

    const {
        deleteBuildHub,
        isDeleting,
        isSuccess: deleteSuccess,
    } = useBuildHubDelete({
        shouldNavigateOnSuccess: true,
    });
    const {
        leaveBuildHub,
        isLeaving,
        isSuccess: leaveSuccess,
    } = useBuildHubLeave({
        shouldNavigateOnSuccess: true,
    });

    const handleDelete = async () => {
        try {
            await deleteBuildHub(buildHubId);
        } catch (error) {
            console.error('Failed to delete build hub:', error);
            // Error is already handled by the hook and will be displayed via the error state
        }
    };

    const handleLeave = async () => {
        try {
            await leaveBuildHub(buildHubId);
        } catch (error) {
            console.error('Failed to leave build hub:', error);
            // Error is already handled by the hook and will be displayed via the error state
        }
    };

    useEffect(() => {
        if (leaveSuccess) {
            setOpenLeaveConfirmation(false);
            toast.success(`You have left the project.`, {
                action: {
                    label: 'Dismiss',
                    onClick: () => toast.dismiss(),
                },
            });
        }
    }, [leaveSuccess]);

    useEffect(() => {
        if (deleteSuccess) {
            setOpenDeleteConfirmation(false);
            toast.success(`Project deleted successfully`, {
                action: {
                    label: 'Dismiss',
                    onClick: () => toast.dismiss(),
                },
            });
        }
    }, [deleteSuccess]);

    const onCopyLink = () => {
        copyToClipboard(
            `${location.host}${makeBuildHubUrl(buildHubUrl)}`
        ).finally(() => {
            setIsPopoverOpen(false);
        });
    };

    const onManageAccess = () => {
        setIsManageCollabratorModalOpen(true);
        setIsPopoverOpen(false);
    };

    const onEditBuildHub = () => {
        setIsEditBuildHubModal({
            isOpen: true,
            buildHubId,
        });
        setIsPopoverOpen(false);
    };

    const editAction: (ItemMenuAction | ItemMenuSeparator)[] =
        userPermissions.canEdit
            ? [
                  { type: 'separator' },
                  {
                      label: 'Edit',
                      icon: <PencilIcon className="mr-2 h-4 w-4" />,
                      onClick: onEditBuildHub,
                      type: 'action',
                  },
              ]
            : [];

    const manageAccessAction: (ItemMenuAction | ItemMenuSeparator)[] =
        userPermissions.canEdit
            ? [
                  { type: 'separator' },
                  {
                      label: 'Manage Access',
                      icon: <UsersIcon className="mr-2 h-4 w-4" />,
                      onClick: onManageAccess,
                      type: 'action',
                  },
              ]
            : [];

    const deleteAction: (ItemMenuAction | ItemMenuSeparator)[] =
        userPermissions.canDelete
            ? [
                  { type: 'separator' },
                  {
                      label: isDeleting ? 'Deleting...' : 'Delete',
                      icon: <TrashIcon className="mr-2 h-4 w-4" />,
                      onClick: () => setOpenDeleteConfirmation(true),
                      type: 'action',
                      disabled: isDeleting,
                  },
              ]
            : [];

    const leaveAction: (ItemMenuAction | ItemMenuSeparator)[] = !isOwner
        ? [
              { type: 'separator' },
              {
                  label: isLeaving ? 'Leaving...' : 'Leave Project',
                  icon: <UserRoundX className="mr-2 h-4 w-4" />,
                  onClick: () => setOpenLeaveConfirmation(true),
                  type: 'action',
                  disabled: isLeaving,
              },
          ]
        : [];

    return (
        <>
            <ItemMenu
                setIsPopoverOpen={setIsPopoverOpen}
                isPopoverOpen={isPopoverOpen}
                actions={[
                    {
                        label: 'Copy Link',
                        icon: <CopyIcon className="mr-2 h-4 w-4" />,
                        onClick: onCopyLink,
                        type: 'action',
                    },
                    ...editAction,
                    ...manageAccessAction,
                    ...deleteAction,
                    ...leaveAction,
                ]}
            />
            <AlertDialog
                title="Are you absolutely sure?"
                description="This action cannot be undone. This will permanently delete this project and its associated information and files."
                onConfirm={handleDelete}
                controlled={{
                    open: openDeleteConfirmation,
                    setOpen: setOpenDeleteConfirmation,
                }}
            />
            <AlertDialog
                title="Are you absolutely sure?"
                description="If you leave this project, design boards that you've linked to this project will remain here unless you unlink them first."
                onConfirm={handleLeave}
                controlled={{
                    open: openLeaveConfirmation,
                    setOpen: setOpenLeaveConfirmation,
                }}
            />
        </>
    );
};
